import parsePhoneNumberFromString from 'libphonenumber-js';
import React from 'react';
import { isValidABN, REGEX } from '@admiin-com/ds-common';
import { CSVMapClientFormData } from './CsvClientMapFields';

import Papa from 'papaparse';
export type FailedRowDetail = {
  no: number;
  value: string;
  message: string;
  type: 'warning' | 'error';
};
interface useValidateClientDataProps {
  file?: File;
  fields: {
    firstName?: string;
    lastName?: string;
    companyName?: string;
    email?: string;
    mobile?: string;
    phone?: string;
    taxNumber?: string;
    companyNumber?: string;
    name?: string;
    address?: string;
    address1?: string;
    address2?: string;
    city?: string;
    state?: string;
    stateCode?: string;
    postalCode?: string;
    country?: string;
    countryCode?: string;
    clientCode?: string;
    clientGroupCode?: string;
  };
}

export function useCsvClientValidation({
  file,
  fields,
}: useValidateClientDataProps) {
  const [successedRow, setSuccessedRow] = React.useState<number>(0);
  const [failedRowDetails, setFailedRowDetail] = React.useState<
    Array<FailedRowDetail>
  >([]);
  const [failedCount, setFailedCount] = React.useState<number>(0);
  const [warningCount, setWarningCount] = React.useState<number>(0);

  const [fieldNames, setFieldNames] = React.useState<string[]>([]);

  const validateContactData = ({
    firstName,
    lastName,
    mobile,
    taxNumber,
    email,
    name,
  }: CSVMapClientFormData) => {
    const errors: Array<any> = [];
    const warnings: Array<any> = [];

    if (taxNumber !== '' && !isValidABN(taxNumber)) {
      errors.push({
        field: 'taxNumber',
        message: 'Invalid ABN',
        type: 'error',
      });
    }
    if (name === '' && lastName === '' && firstName !== '') {
      errors.push({
        field: 'lastName',
        message: 'LastName name is required',
        type: 'error',
      });
    }
    if (name === '' && lastName !== '' && firstName === '') {
      errors.push({
        field: 'firstName',
        message: 'FirstName name is required',
        type: 'error',
      });
    }
    // Email is now optional but must be valid if provided
    if (email !== '' && !REGEX.EMAIL.test(email)) {
      errors.push({
        field: 'email',
        message: 'Invalid email format',
        type: 'error',
      });
    }

    if (mobile !== '') {
      if (!mobile || !parsePhoneNumberFromString(mobile, 'AU')) {
        console.log(
          'parsePhoneNumberFromString:',
          parsePhoneNumberFromString(mobile, 'AU')
        );
        errors.push({
          field: 'mobile',
          message: 'Invalid mobile number',
          type: 'warning',
        });
      }
    }
    return { errors, warnings };
  };

  //
  React.useEffect(() => {
    if (file) {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (result) => {
          const records = result.data;
          if (records.length > 0) {
            let succeedRecordCount = 0;
            let failedRecordCount = 0;
            let warningRecordCount = 0;
            const errorsReport: Array<FailedRowDetail> = [];
            records.forEach((record: any, index) => {
              const firstName =
                fields.firstName && fields.firstName !== 'Ignore column'
                  ? record?.[fields.firstName]
                  : '';
              const lastName =
                fields.lastName && fields.lastName !== 'Ignore column'
                  ? record?.[fields.lastName]
                  : '';
              const mobile =
                fields.mobile && fields.mobile !== 'Ignore column'
                  ? record?.[fields.mobile]
                  : '';
              const phone =
                fields.phone && fields.phone !== 'Ignore column'
                  ? record?.[fields.phone]
                  : '';
              const email =
                fields.email && fields.email !== 'Ignore column'
                  ? record?.[fields.email]
                  : '';
              const name =
                fields.name && fields.name !== 'Ignore column'
                  ? record?.[fields.name]
                  : '';
              const taxNumber =
                fields.taxNumber && fields.taxNumber !== 'Ignore column'
                  ? record?.[fields.taxNumber]
                  : '';
              const companyNumber =
                fields.companyNumber && fields.companyNumber !== 'Ignore column'
                  ? record?.[fields.companyNumber]
                  : '';
              const address =
                fields.address && fields.address !== 'Ignore column'
                  ? record?.[fields.address]
                  : '';
              const address1 =
                fields.address1 && fields.address1 !== 'Ignore column'
                  ? record?.[fields.address1]
                  : '';
              const address2 =
                fields.address2 && fields.address2 !== 'Ignore column'
                  ? record?.[fields.address2]
                  : '';
              const city =
                fields.city && fields.city !== 'Ignore column'
                  ? record?.[fields.city]
                  : '';
              const state =
                fields.state && fields.state !== 'Ignore column'
                  ? record?.[fields.state]
                  : '';
              const stateCode =
                fields.stateCode && fields.stateCode !== 'Ignore column'
                  ? record?.[fields.stateCode]
                  : '';
              const postalCode =
                fields.postalCode && fields.postalCode !== 'Ignore column'
                  ? record?.[fields.postalCode]
                  : '';
              const country =
                fields.country && fields.country !== 'Ignore column'
                  ? record?.[fields.country]
                  : '';
              const countryCode =
                fields.countryCode && fields.countryCode !== 'Ignore column'
                  ? record?.[fields.countryCode]
                  : '';

              const clientCode =
                fields.clientCode && fields.clientCode !== 'Ignore column'
                  ? record?.[fields.clientCode]
                  : '';

              const clientGroupCode =
                fields.clientGroupCode &&
                fields.clientGroupCode !== 'Ignore column'
                  ? record?.[fields.clientGroupCode]
                  : '';
              const { errors } = validateContactData({
                firstName,
                lastName,
                mobile,
                phone,
                email,
                taxNumber,
                companyNumber,
                name,
                address,
                address1,
                address2,
                city,
                state,
                stateCode,
                postalCode,
                country,
                countryCode,
                clientCode,
                clientGroupCode,
              });
              if (errors.length > 0) {
                let hasError = false;
                for (const error of errors) {
                  const field =
                    fields[error.field as keyof CSVMapClientFormData];
                  errorsReport.push({
                    no: index,
                    value: field ? record?.[field] : '',
                    message: error.message,
                    type: error.type,
                  });

                  if (error.type === 'error') {
                    hasError = true;
                  }
                }
                hasError ? failedRecordCount++ : warningRecordCount++;
              } else {
                succeedRecordCount++;
              }
            });
            setSuccessedRow(succeedRecordCount);
            setFailedCount(failedRecordCount);
            setWarningCount(warningRecordCount);
            setFailedRowDetail(errorsReport);
          }
        },
      });
    }
  }, [file, fields]);

  //
  React.useEffect(() => {
    if (file) {
      Papa.parse(file, {
        header: false,
        skipEmptyLines: true,
        complete: (result: any) => {
          const records = result.data;
          if (records.length > 0) {
            const fieldNames: string[] = records[0];
            setFieldNames(fieldNames);
          }
        },
      });
    }
  }, [file]);

  return {
    successedRow,
    failedRowDetails,
    failedCount,
    warningCount,
    fieldNames,
  };
}
